import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  ExportOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Popconfirm,
  Popover,
  Tooltip,
  Upload,
} from 'antd';
import Cookies from 'js-cookie';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';

import { fetchAPI, getFullAPIURLWithEndpoint } from '../../utils/api';
import OrderRejectionForm from './OrderRejectionForm';
import { OrderOperationStatus } from './utils';
import { USER_TEAMS } from '../../constants/CONSTANTS';

const confirmColor = { color: '#38c61b', borderColor: '#38c61b' };

const OrderOperationStatusActions = props => {
  const operationStatus = props.record?.operation_status?.split('_')?.join(' ');
  const [loadingBtn, setLoadingBtn] = useState('');
  const [modalState, setModalState] = useState({
    title: '',
    visible: false,
    signed: false,
  });
  const [fileList, setFileList] = useState([]);
  const [reasonPopOver, setReasonPopOver] = useState(false);

  const isBtnDisabled = keyStr => {
    return !!loadingBtn && keyStr !== loadingBtn;
  };

  const changeOrderOperationStatus = async (
    newOperationStatus,
    attachments = null,
    rejectReason: any = {}
  ) => {
    let payload: any = {
      status: newOperationStatus?.split(' ')?.join('_'),
      attachments: !!attachments ? attachments : undefined,
    };
    if (
      newOperationStatus === OrderOperationStatus.REJECTED ||
      newOperationStatus === OrderOperationStatus.PENDING_APPROVAL
    ) {
      payload.reason = rejectReason?.reason;
      payload.spid = isEmpty(rejectReason?.spid_number)
        ? undefined
        : rejectReason?.spid_number[0];
    }
    try {
      await fetchAPI(`orders/${props.record?.id}/set-operation-status/action`, {
        method: 'POST',
        payload,
      });
      props.refreshList();
    } catch (error) {
      message.error('Failed change order operation status');
      if (error?.error_message || error?.errorMessage) {
        message.error(error?.error_message || error?.errorMessage);
      }
    }
    setLoadingBtn('');
  };

  const completeSaidViaRicShortcut = async () => {
    setLoadingBtn('ric-shortcut');
    try {
      await fetchAPI(`orders/${props.record?.id}/complete/ric-shortcut`, {
        method: 'POST',
      });
      props.refreshList();
    } catch (error) {
      message.error('Failed to complete SAID via RIC shortcut');
      if (error?.error_message || error?.errorMessage) {
        message.error(error?.error_message || error?.errorMessage);
      }
    }
    setLoadingBtn('');
  };

  // only operations-team or RIC-group users may complete without DO / signed DO
  const ricShortcutButton =
    props.user?.team === USER_TEAMS.OPERATIONS ||
    props.user?.group === 'RIC' ? (
      <Tooltip title="Complete this SAID without uploading a DO or signed DO. Recorded as completed via the RIC shortcut.">
        <Popconfirm
          title={`Complete SAID ${props.record?.region_ref_number} without DO / signed DO?`}
          okText="Yes"
          cancelText="No"
          onConfirm={completeSaidViaRicShortcut}
        >
          <Button
            style={confirmColor}
            icon={<CheckCircleOutlined />}
            loading={loadingBtn === 'ric-shortcut'}
            disabled={isBtnDisabled('ric-shortcut')}
          >
            Complete SAID (No DO)
          </Button>
        </Popconfirm>
      </Tooltip>
    ) : null;

  if (operationStatus === OrderOperationStatus.ORDER_CREATED) {
    return (
      <Tooltip title="Confirm Order">
        <Button
          icon={<CheckOutlined />}
          style={confirmColor}
          loading={loadingBtn === 'confirm-order'}
          disabled={isBtnDisabled('confirm-order')}
          onClick={() => {
            setLoadingBtn('confirm-order');
            changeOrderOperationStatus(OrderOperationStatus.PENDING_APPROVAL);
          }}
        >
          Confirm
        </Button>
      </Tooltip>
    );
  }

  if (operationStatus === OrderOperationStatus.PENDING_APPROVAL) {
    return (
      <>
        <Tooltip title="Approve Order">
          <Button
            icon={<CheckOutlined />}
            style={confirmColor}
            loading={loadingBtn === 'approve-order'}
            disabled={isBtnDisabled('approve-order')}
            onClick={() => {
              setLoadingBtn('approve-order');
              changeOrderOperationStatus(OrderOperationStatus.APPROVED);
            }}
          >
            Approve
          </Button>
        </Tooltip>
        <Tooltip title="Reject Order">
          <Popover
            content={
              <>
                <OrderRejectionForm
                  onSubmit={values => {
                    setLoadingBtn('reject-order');
                    setReasonPopOver(false);
                    changeOrderOperationStatus(
                      OrderOperationStatus.REJECTED,
                      null,
                      values
                    );
                  }}
                />
              </>
            }
            title={
              <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                Please Provide Information for Rejection
              </div>
            }
            trigger="click"
            placement="bottomLeft"
            visible={reasonPopOver}
            onVisibleChange={val => {
              setReasonPopOver(val);
            }}
          >
            <Button
              icon={<CloseOutlined />}
              danger
              loading={loadingBtn === 'reject-order'}
              disabled={isBtnDisabled('reject-order')}
            >
              Reject
            </Button>
          </Popover>
        </Tooltip>
      </>
    );
  }

  if (
    [
      OrderOperationStatus.APPROVED,
      OrderOperationStatus.COMPLETED_TENTATIVE,
    ].indexOf(operationStatus) !== -1
  ) {
    return (
      <>
        <Tooltip title="Sent Order">
          <Button
            icon={<ExportOutlined />}
            style={confirmColor}
            loading={loadingBtn === 'sent-order'}
            disabled={isBtnDisabled('sent-order')}
            onClick={() => {
              setLoadingBtn('sent-order');
              changeOrderOperationStatus(OrderOperationStatus.PENDING_DELIVERY);
            }}
          >
            Sent
          </Button>
        </Tooltip>
      </>
    );
  }

  if (
    operationStatus === OrderOperationStatus.PENDING_DELIVERY ||
    operationStatus === OrderOperationStatus.PENDING_DO_UPLOAD
  ) {
    return (
      <>
        {operationStatus !== OrderOperationStatus.PENDING_DO_UPLOAD && (
          <Tooltip title="Upload DO">
            <Button
              style={{ color: '#38c61b', borderColor: 'rgb(119, 119, 119)' }}
              icon={<UploadOutlined />}
              onClick={() =>
                setModalState({
                  title: `Upload DO (${props.record?.region_ref_number})`,
                  visible: true,
                  signed: false,
                })
              }
              loading={loadingBtn === 'upload-do'}
              disabled={isBtnDisabled('upload-do')}
            >
              Upload DO
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Upload Signed DO">
          <Button
            style={{ color: '#38c61b', borderColor: 'rgb(119, 119, 119)' }}
            icon={<CloudUploadOutlined />}
            onClick={() =>
              setModalState({
                title: `Upload Signed DO (${props.record?.region_ref_number})`,
                visible: true,
                signed: true,
              })
            }
            loading={loadingBtn === 'upload-signed-do'}
            disabled={isBtnDisabled('upload-signed-do')}
          >
            Upload Signed DO
          </Button>
        </Tooltip>
        {ricShortcutButton}
        <Modal
          title={modalState.title}
          visible={modalState.visible}
          onCancel={() => setModalState(ms => ({ ...ms, visible: false }))}
          onOk={() => {
            let newOperationStatus = OrderOperationStatus.PENDING_DO_UPLOAD;
            if (
              operationStatus === OrderOperationStatus.PENDING_DELIVERY &&
              modalState.signed
            ) {
              newOperationStatus = OrderOperationStatus.UPLOAD_SIGN_DO;
            } else if (
              operationStatus === OrderOperationStatus.PENDING_DO_UPLOAD
            ) {
              newOperationStatus = OrderOperationStatus.PENDING_SIGN_DO_UPLOAD;
            }

            setLoadingBtn(modalState.signed ? 'upload-signed-do' : 'upload-do');
            setModalState(ms => ({ ...ms, visible: false }));
            changeOrderOperationStatus(newOperationStatus, fileList);
          }}
          destroyOnClose
        >
          <Upload
            showUploadList={{
              showDownloadIcon: true,
              showRemoveIcon: true,
            }}
            withCredentials
            multiple
            listType="text"
            data={{ type: modalState.signed ? 'order-signed-do' : 'order-do' }}
            action={getFullAPIURLWithEndpoint('upload', 'v1.0')}
            headers={{ 'X-CSRF-TOKEN': Cookies.get('csrf_access_token') || '' }}
            onChange={obj => {
              setFileList(obj.fileList.map(f => f.response?.id));
            }}
          >
            <Button>
              <UploadOutlined /> Upload
            </Button>
          </Upload>
        </Modal>
      </>
    );
  }

  if (
    operationStatus === OrderOperationStatus.PENDING_SIGN_DO_UPLOAD ||
    operationStatus === OrderOperationStatus.UPLOAD_SIGN_DO
  ) {
    return (
      <>
        <Tooltip title="Mark as Completed">
          <Button
            style={confirmColor}
            icon={<CheckCircleOutlined />}
            loading={loadingBtn === 'complete-order'}
            disabled={isBtnDisabled('complete-order')}
            onClick={() => {
              setLoadingBtn('complete-order');
              changeOrderOperationStatus(OrderOperationStatus.COMPLETED);
            }}
          >
            Complete
          </Button>
        </Tooltip>
        {ricShortcutButton}
      </>
    );
  }

  return null;
};

export default OrderOperationStatusActions;
